import { useState, useEffect } from 'react';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { BAKERY_LOCAL } from '../utils/bakeryLocal';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, SparklesIcon, CakeIcon, StarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const galleryImages = [
  '/assets/images/products/product-chocolate-cake-ganache-ferrero-rocher.webp',
  '/assets/images/products/product-birthday-cake-first-birthday-teddy-bear.webp',
  '/assets/images/products/product-cheesecake-slice-caramel-biscoff.webp',
  '/assets/images/products/product-rainbow-ombre-cake-pastel.webp',
  '/assets/images/products/product-churros-stack-sugar-cinnamon.webp',
  '/assets/images/products/product-chocolate-covered-strawberries.webp',
  '/assets/images/products/product-butter-tart.webp',
  '/assets/images/products/product-donuts-cinnamon-sugar.webp',
];

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  color: string;
  has_landing_page: boolean;
  is_active: boolean;
}

export default function About() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.about,
    og: {
      type: 'website',
      title: PAGE_SEO.about.title,
      description: PAGE_SEO.about.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [eventCategories, setEventCategories] = useState<EventCategory[]>([]);

  // Fetch active event categories
  useEffect(() => {
    const fetchEventCategories = async () => {
      try {
        const response = await fetch('/api/event-categories?active=true');
        if (response.ok) {
          const data = await response.json();
          setEventCategories(data.categories || []);
        }
      } catch (err) {
        console.error('Failed to fetch event categories:', err);
      }
    };
    fetchEventCategories();
  }, []);

  const values = [
    {
      icon: HeartIcon,
      title: 'Made with Love',
      description: 'Every treat is crafted with care, attention to detail, and a whole lot of love.',
      image: '/assets/images/products/product-chocolate-cake-strawberries.webp',
    },
    {
      icon: SparklesIcon,
      title: 'Quality Ingredients',
      description: 'We use only the finest ingredients to ensure every bite is absolutely delicious.',
      image: '/assets/images/products/product-cheesecake-slices-assorted-toppings.webp',
    },
    {
      icon: CakeIcon,
      title: 'Custom Creations',
      description: 'From simple treats to elaborate custom cakes, we bring your vision to life.',
      image: '/assets/images/products/product-wedding-cake-mr-and-mrs.webp',
    },
    {
      icon: StarIcon,
      title: 'Local Favorites',
      description: 'Proudly serving the Barrie community with fresh baked goods every day.',
      image: '/assets/images/products/stuffed-cookie.webp',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="gradient-primary text-white section-padding">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-display font-bold mb-4"
          >
            About Nanny B's Bakery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-100 max-w-3xl mx-auto"
          >
            A family-owned bakery in Barrie, Ontario, dedicated to creating fresh, delicious baked goods that bring joy to every occasion.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Welcome to Nanny B's Bakery, where every treat is made with love and the finest ingredients. 
                  Located in south Barrie at Commerce Park Drive, we've been serving our community with fresh baked 
                  goods, custom cakes, and delicious pastries.
                </p>
                <p>
                  Our passion for baking started with a simple love for creating treats that bring people together. 
                  Whether it's a birthday celebration, wedding, or just a sweet treat to brighten your day, we're 
                  here to make it special.
                </p>
                <p>
                  From our signature custom cakes to our daily fresh pastries, every item is crafted with attention 
                  to detail and a commitment to quality. We believe that great baking is about more than just 
                  ingredients—it's about creating moments of joy.
                </p>
                <p>
                  Tammy (Nanny B) bakes on site at 62 Commerce Park Drive, Unit L. Custom cakes are made to the
                  photo and notes you send — typically with 72 hours notice. That first-hand shop experience is
                  what customers review on Google, and it is how we work every week.{' '}
                  <Link to="/bakery-in-barrie#common-questions" className="text-primary-600 underline">
                    Common questions about our Barrie bakery
                  </Link>
                  .
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/assets/images/exterior/storefront-exterior.webp"
                  alt="Nanny B's Bakery Storefront"
                  className="w-full h-auto"
                />
              </div>
              {/* Floating product images */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-xl overflow-hidden shadow-xl hidden md:block">
                <img 
                  src="/assets/images/products/product-chocolate-cake-ganache-ferrero-rocher.webp"
                  alt="Chocolate Cake"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -right-6 w-28 h-28 rounded-xl overflow-hidden shadow-xl hidden md:block">
                <img 
                  src="/assets/images/products/product-butter-tart.webp"
                  alt="Butter Tart"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-bakery-cream">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
              What We Stand For
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our values guide everything we do, from selecting ingredients to crafting each treat.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card overflow-hidden"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={value.image}
                    alt={value.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 text-center">
                  <value.icon className="h-10 w-10 text-primary-600 mx-auto mb-3" />
                  <h3 className="text-lg font-display font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
              Our Creations
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A glimpse of our delicious baked goods
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((src, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="aspect-square rounded-xl overflow-hidden shadow-lg group"
              >
                <img
                  src={src}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services & Events Section */}
      {eventCategories.length > 0 && (
        <section className="section-padding bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
                Services & Events
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Join us for special events and classes at the bakery!
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {eventCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {category.has_landing_page ? (
                    <Link
                      to={`/events/${category.slug}`}
                      className="block bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                      style={{ borderTopColor: category.color, borderTopWidth: '4px' }}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-2xl font-display font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                            {category.name}
                          </h3>
                          <ArrowRightIcon className="w-6 h-6 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                        </div>
                        {category.description && (
                          <p className="text-gray-600 mb-4 line-clamp-2">
                            {category.description}
                          </p>
                        )}
                        {category.image_url && (
                          <div className="aspect-video rounded-lg overflow-hidden mb-4">
                            <img
                              src={category.image_url}
                              alt={category.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <span className="text-primary-600 font-semibold group-hover:underline">
                          Learn More →
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <div
                      className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-md p-6"
                      style={{ borderTopColor: category.color, borderTopWidth: '4px' }}
                    >
                      <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                        {category.name}
                      </h3>
                      {category.description && (
                        <p className="text-gray-600 mb-4">
                          {category.description}
                        </p>
                      )}
                      {category.image_url && (
                        <div className="aspect-video rounded-lg overflow-hidden mb-4">
                          <img
                            src={category.image_url}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:underline"
                      >
                        Contact Us to Learn More
                        <ArrowRightIcon className="w-5 h-5" />
                      </a>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Specialties Section */}
      <section className="section-padding bg-gray-900 text-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-display font-bold mb-6">
                Our Specialties
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="text-2xl">🎂</span>
                  <div>
                    <h3 className="font-semibold text-lg">Custom Cakes</h3>
                    <p className="text-gray-300">Birthday, wedding, and special occasion cakes made to order</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-2xl">🍪</span>
                  <div>
                    <h3 className="font-semibold text-lg">Stuffed Cookies</h3>
                    <p className="text-gray-300">Our signature oversized cookies with delicious fillings</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-2xl">🥧</span>
                  <div>
                    <h3 className="font-semibold text-lg">Butter Tarts</h3>
                    <p className="text-gray-300">Classic Canadian butter tarts made fresh daily</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-2xl">🍰</span>
                  <div>
                    <h3 className="font-semibold text-lg">Cheesecakes</h3>
                    <p className="text-gray-300">Creamy cheesecakes with various toppings</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="/assets/images/products/product-birthday-cake-jungle-safari-animals.webp"
                alt="Custom Cake"
                className="rounded-xl shadow-xl"
              />
              <img 
                src="/assets/images/products/product-christmas-seasons-greetings-cake-nutcrackers.webp"
                alt="Holiday Cake"
                className="rounded-xl shadow-xl mt-8"
              />
              <img 
                src="/assets/images/products/product-heart-cake-white-ruffled-borders.webp"
                alt="Heart Cake"
                className="rounded-xl shadow-xl -mt-8"
              />
              <img 
                src="/assets/images/products/product-gender-reveal-cake-boy-or-girl.webp"
                alt="Gender Reveal Cake"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visit Us Section */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
              Visit Us Today
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              We'd love to welcome you to our bakery! Stop by to see our daily selection of fresh baked goods.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:7055034966" className="btn-primary">
                Call Us
              </a>
              <a
                href={BAKERY_LOCAL.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline"
              >
                Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
